import { FormEvent, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAccount } from "../../app/account";
import { isEmail } from "../../core/account/credentials";
import { accountErrorKind, AccountErrorKind } from "../../core/account/types";
import { buttonClass, LoadingScreen } from "../../components/StatusScreen";
import { en } from "../../i18n/en";
import { AccountPage, Field, FormError, FormNote } from "./AccountPage";
import { NewPasswordForm } from "./NewPasswordForm";

const t = en.account.reset;

/**
 * Forgot password (specs/11 FR-61). Signed out, it sends a reset link; opened from that link,
 * which signs the person in, it sets the new password.
 */
export function ResetPasswordScreen() {
  const { state, clearLinkEvent } = useAccount();
  const navigate = useNavigate();

  if (state.status === "checking") return <LoadingScreen />;
  if (state.status === "signed-in") {
    return (
      <AccountPage title={t.newTitle}>
        <NewPasswordForm
          label={t.newPassword}
          submitLabel={t.save}
          onSaved={() => {
            clearLinkEvent();
            navigate("/", { replace: true });
          }}
        />
      </AccountPage>
    );
  }
  return <RequestReset />;
}

/** Errors worth showing. Anything else gets the same message as success, so the form doesn't
 * tell whether an email has an account. */
const SHOWN: AccountErrorKind[] = ["rate-limit", "network"];

function RequestReset() {
  const { linkError, clearLinkError, services } = useAccount();
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState(false);
  const [error, setError] = useState<AccountErrorKind | null>(null);
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    clearLinkError();
    setError(null);
    const valid = isEmail(email);
    setEmailError(!valid);
    if (!valid) return;
    setBusy(true);
    try {
      await services.account.requestPasswordReset(email.trim());
      setSent(true);
    } catch (err) {
      const kind = accountErrorKind(err);
      if (SHOWN.includes(kind)) setError(kind);
      else setSent(true);
    }
    setBusy(false);
  };

  return (
    <AccountPage title={t.title}>
      <div className="flex flex-col gap-4">
        {linkError === "link-expired" && <FormError>{t.expired}</FormError>}
        {error && <FormError>{en.account.errors[error]}</FormError>}
        {sent ? (
          <FormNote>{t.sent}</FormNote>
        ) : (
          <>
            <p className="text-slate-300">{t.requestBody}</p>
            <form onSubmit={submit} noValidate className="flex flex-col gap-4">
              <Field
                label={t.email}
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                error={emailError ? en.account.emailInvalid : null}
              />
              <button type="submit" disabled={busy} className={buttonClass}>
                {busy ? t.sending : t.send}
              </button>
            </form>
            <p className="text-xs text-slate-400">{t.googleNote}</p>
          </>
        )}
        <Link to="/sign-in" className="text-center text-sm text-sky-400 underline">
          {t.backToSignIn}
        </Link>
      </div>
    </AccountPage>
  );
}
